/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useRef, useState, useEffect, forwardRef, useMemo} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {
    useMultiStyleConfig,
    Box,
    Link as ChakraLink,
    IconButton,
    Badge,
    omitThemingProps,
    StylesProvider,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    MenuDivider
} from '@chakra-ui/react'

import useBasket from '../../commerce-api/hooks/useBasket'

import {BrandLogo, BasketIcon, HamburgerIcon, StorefinderIcon, SignoutIcon} from '../icons'
import {STORE_LOCATOR_HREF, CONTACT_US_HREF, HYBRID_CUSTOMER_SERVICE} from '../../constants'

import useMultiSite from '../../hooks/use-multi-site'
import useEffectEvent from '../../hooks/use-effect-event'
import LocaleSelector from '../locale-selector'
import HybridLink from '../hybrid-link'
import HeaderSearch from './header-search'
import useCustomer from '../../commerce-api/hooks/useCustomer'
import LoadingSpinner from '../loading-spinner'
import useAsync from '../../commerce-api/hooks/useAsync'
import MyStokkeNavigationDropdown from '../../pages/my-stokke/partials/navigation-dropdown'
import CompositeAccountIcon from '../icons/composite-account-icon'
import useOcapiSite from '../../commerce-api/hooks/useOcapiSite'

/**
 * Extract the AccountButton component so it can be reused as menu button and standalone button
 */
const AccountButton = forwardRef((props, ref) => {
    const {formatMessage} = useIntl()
    const customer = useCustomer()
    const firstName = useMemo(
        () => (customer.firstName !== '' ? customer.firstName : ''),
        [customer]
    )

    const styles = useMultiStyleConfig('Header', props)
    return (
        <IconButton
            {...props}
            ref={ref}
            icon={<CompositeAccountIcon isActive={customer.isRegistered} />}
            variant="unstyled"
            {...styles.accountButton}
            aria-label={formatMessage({
                id: 'header.button.assistive_msg.my_account',
                defaultMessage: 'My Stokke'
            })}
            data-testid="header-account-icon"
            // cannot use the same trigger for tooltip and popover,
            // so use the title attribute to show tooltip on hover
            title={
                customer.isRegistered
                    ? formatMessage(
                          {
                              id: 'global.account.tooltip',
                              defaultMessage: 'Hi, {firstName}'
                          },
                          {firstName}
                      )
                    : // no label, no tooltip
                      null
            }
        />
    )
})
if (process.env.NODE_ENV !== 'production') {
    AccountButton.displayName = 'AccountButton'
}
AccountButton.propTypes = {
    onClick: PropTypes.func
}

/**
 * The header is the main source for accessing
 * navigation, search, basket, and other
 * important information and actions. It persists
 * on the top of your application and will
 * respond to changes in device size.
 *
 * To customize the styles, update the themes
 * in theme/components/project/header.js
 * @param  props
 * @param   {func} props.onMenuClick click event handler for menu button
 * @param   {func} props.onLogoClick click event handler for menu button
 * @param   {object} props.searchInputRef reference of the search input
 * @param   {func} props.onMyAccountClick click event handler for my account button
 * @param   {func} props.onMyCartClick click event handler for my cart button
 * @return  {React.ReactElement} - Header component
 */
const Header = forwardRef((props, ref) => {
    const intl = useIntl()
    const {myStokkeEnabled} = useOcapiSite().preferences
    const customer = useCustomer()
    const basket = useBasket()
    const {site, buildUrl} = useMultiSite()
    // there is some bug that scrolls whole page with 1-2 px when opening the header
    const headerHideShowOffset = 5

    const styles = useMultiStyleConfig('Header', props)
    const {
        children,
        onMenuClick,
        onMyAccountClick,
        onMyAccountMenuOpen,
        onLogoClick,
        onMyCartClick,
        onSearchClick,
        menuIcon: MenuIcon = HamburgerIcon,
        ...ownProps
    } = omitThemingProps(props)

    /* When scrolling downwards - hide header, upwards - show header */
    const scrollTop = useRef(0)
    const [hideHeader, setHideHeader] = useState(false)

    const onScroll = useEffectEvent(() => {
        requestAnimationFrame(() => {
            // show header when on top of pdp
            if (scrollY === 0) {
                setHideHeader(false)
                return
            }

            if (
                scrollY > ref?.current?.clientHeight &&
                Math.abs(scrollY - scrollTop.current) > headerHideShowOffset
            ) {
                setHideHeader(scrollY > scrollTop.current)

                scrollTop.current = scrollY
            }
        })
    })
    useEffect(() => {
        addEventListener('scroll', onScroll, {passive: true})

        /* Cleanup event listener */
        return () => removeEventListener('scroll', onScroll)
    }, [onScroll])

    const storeLocatorHref = useMemo(() => buildUrl(STORE_LOCATOR_HREF), [buildUrl])

    // my-stokke page will redirect to home in case logout happens on my-stokke page,
    // otherwise keep the current page
    const {loading: showLoading, execute: onSignOutClick} = useAsync(customer.logout)

    return (
        <StylesProvider value={styles}>
            <Box
                bg="stokkeCore.white"
                // this is a workaround to prevent header shifts on modal/drawer open
                paddingInlineEnd="var(--removed-body-scroll-bar-size)"
            >
                {showLoading && <LoadingSpinner wrapperStyles={{height: '100vh'}} />}
                <Box
                    ref={ref}
                    {...styles.container}
                    {...ownProps}
                    marginTop={hideHeader && `${-ref?.current?.clientHeight}px`}
                >
                    <Box {...styles.mainContainer}>
                        <IconButton
                            aria-label={intl.formatMessage({
                                id: 'header.button.assistive_msg.logo',
                                defaultMessage: 'Logo'
                            })}
                            icon={<BrandLogo {...styles.logo} />}
                            {...styles.brandButton}
                            variant="unstyled"
                            onClick={onLogoClick}
                        />
                        <Box {...styles.leftContent}>
                            <IconButton
                                aria-label={intl.formatMessage({
                                    id: 'header.button.assistive_msg.menu',
                                    defaultMessage: 'Menu'
                                })}
                                icon={<MenuIcon {...styles.icon} />}
                                variant="unstyled"
                                {...styles.menuButton}
                                onClick={onMenuClick}
                            />
                            <Box {...styles.bodyContainer}>{children}</Box>
                        </Box>
                        <Box {...styles.rightContent}>
                            <HeaderSearch onShow={onSearchClick} />

                            <AccountButton
                                display={
                                    myStokkeEnabled && customer.isRegistered ? {lg: 'none'} : null
                                }
                                onClick={onMyAccountClick}
                            />

                            {myStokkeEnabled && customer.isRegistered && (
                                <Menu
                                    placement="bottom-end"
                                    variant="menu"
                                    onOpen={onMyAccountMenuOpen}
                                >
                                    <MenuButton
                                        display={{base: 'none', lg: 'block'}}
                                        as={AccountButton}
                                    />

                                    <MenuList
                                        rootProps={{display: {base: 'none', lg: 'block'}}}
                                        // offset to the right to accommodate the menu button chevron
                                        // 1 unit from gap and 2 units from chevron size
                                        marginInlineEnd={-3}
                                    >
                                        <MyStokkeNavigationDropdown />
                                        <MenuDivider />
                                        <MenuItem icon={<SignoutIcon />} onClick={onSignOutClick}>
                                            <FormattedMessage
                                                id="global.button.log_out"
                                                defaultMessage="Log Out"
                                            />
                                        </MenuItem>
                                    </MenuList>
                                </Menu>
                            )}

                            {site?.isContentOnlySite ? (
                                <IconButton
                                    as="a"
                                    href={storeLocatorHref}
                                    target="_blank"
                                    rel="noopener"
                                    aria-label={intl.formatMessage({
                                        id: 'header.button.assistive_msg.storefinder',
                                        defaultMessage: 'Storefinder'
                                    })}
                                    icon={
                                        <StorefinderIcon
                                            {...styles.icon}
                                            data-testid="header-storefinder-icon"
                                        />
                                    }
                                    variant="unstyled"
                                    {...styles.iconButton}
                                />
                            ) : (
                                <IconButton
                                    aria-label={intl.formatMessage({
                                        id: 'header.button.assistive_msg.my_cart',
                                        defaultMessage: 'My cart'
                                    })}
                                    icon={
                                        <Box {...styles.cartIconContainer}>
                                            <BasketIcon
                                                {...styles.icon}
                                                data-testid="header-basket-icon"
                                            />
                                            {basket?.loaded && (
                                                <Badge {...styles.cartBadge}>
                                                    {basket.itemAccumulatedCount}
                                                </Badge>
                                            )}
                                        </Box>
                                    }
                                    variant="unstyled"
                                    {...styles.cartButton}
                                    onClick={onMyCartClick}
                                />
                            )}
                        </Box>
                    </Box>
                    <Box {...styles.helperContainer}>
                        <HybridLink
                            page={HYBRID_CUSTOMER_SERVICE}
                            to={CONTACT_US_HREF}
                            size="xs"
                            variant="underlineLink"
                        >
                            <FormattedMessage id="global.help" defaultMessage="Help" />
                        </HybridLink>
                        <ChakraLink
                            href={storeLocatorHref}
                            size="xs"
                            isExternal
                            variant="underlineLink"
                        >
                            <FormattedMessage id="global.stores" defaultMessage="Stores" />
                        </ChakraLink>
                        <LocaleSelector variant="underlineLink" />
                    </Box>
                </Box>
            </Box>
        </StylesProvider>
    )
})

if (process.env.NODE_ENV !== 'production') {
    Header.displayName = 'Header'
}

Header.propTypes = {
    children: PropTypes.node,
    onMenuClick: PropTypes.func,
    onLogoClick: PropTypes.func,
    onMyAccountClick: PropTypes.func,
    onMyAccountMenuOpen: PropTypes.func,
    onMyCartClick: PropTypes.func,
    onSearchClick: PropTypes.func,
    searchInputRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({current: PropTypes.elementType})
    ])
}

export default Header
